<template>
  <div class="profile-edit-container py-3 pt-0 p-md-5">
    <h2 class="mb-4 text-primary-emphasis">
      Editar Meu Perfil
    </h2>

    <p class="text-muted mb-4 description-text">
      Atualize suas informações pessoais e de contato. Para alterar a senha, será necessário informar sua senha atual.
    </p>

    <div v-if="authStore.profileLoading" class="text-center py-5">
      <div class="spinner-border text-primary-emphasis" role="status">
        <span class="visually-hidden">Carregando dados do perfil...</span>
      </div>
      <p class="mt-3 text-muted">Carregando suas informações...</p>
    </div>

    <div v-else-if="authStore.profileError" class="text-center py-5">
      <p class="text-danger">
        <i class="fas fa-exclamation-triangle me-2"></i> Não foi possível carregar seus dados de perfil.
      </p>
      <p class="text-muted">Por favor, tente novamente mais tarde.</p>
    </div>

    <form @submit.prevent="requestSaveConfirmation" class="profile-edit-form" v-else>
      <div class="mb-3">
        <label for="name" class="form-label">Nome Completo</label>
        <input 
          type="text" 
          id="name" 
          class="form-control" 
          v-model="name" 
          placeholder="Seu nome completo"
          @input="validateName" 
          :disabled="authStore.profileSaving"
          required
        />
        <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div> 
      </div>

      <div class="row g-3">
        <div class="col-md-6">
          <div class="mb-3 mb-md-0">
            <label for="email" class="form-label">E-mail</label>
            <input 
              type="email" 
              id="email" 
              class="form-control" 
              v-model="email" 
              placeholder="seu.email@example.com"
              disabled
              readonly
            />
            <small class="form-text text-muted">Seu e-mail não pode ser alterado por aqui.</small>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3 mb-md-0">
            <label for="phone" class="form-label">Celular</label>
            <input 
              type="tel" 
              id="phone" 
              class="form-control" 
              v-model="phone" 
              placeholder="(99) 99999-9999"
              v-mask="phoneMask"
              @input="validatePhone"
              :disabled="authStore.profileSaving"
              required
            />
            <div v-if="errors.phone" class="text-danger mt-1">{{ errors.phone }}</div>
          </div>
        </div>
      </div>

      <hr class="my-4">

      <h5 class="mb-3 text-primary-emphasis">Alterar Senha (Opcional)</h5>
      <p class="text-muted mb-4 description-text">
        Preencha os campos abaixo apenas se desejar alterar sua senha.
      </p>

      <div class="mb-3">
        <label for="currentPassword" class="form-label">Senha Atual</label>
        <input 
          type="password" 
          id="currentPassword" 
          class="form-control" 
          v-model="currentPassword" 
          placeholder="Digite sua senha atual"
          :disabled="authStore.profileSaving"
          :required="isPasswordChangeActive"
        />
        <div v-if="errors.currentPassword" class="text-danger mt-1">{{ errors.currentPassword }}</div>
      </div>

      <div class="mb-3">
        <label for="password" class="form-label">Nova Senha</label>
        <input 
          type="password" 
          id="password" 
          class="form-control" 
          v-model="password" 
          placeholder="Digite sua nova senha"
          @input="validatePassword"
          :disabled="authStore.profileSaving"
          :required="isPasswordChangeActive"
        />
        <div v-if="errors.password" class="text-danger mt-1">{{ errors.password }}</div>
        <small v-if="isPasswordChangeActive" class="form-text text-muted">A nova senha deve ter no mínimo 6 caracteres.</small>
      </div>

      <div class="mb-3">
        <label for="confirmPassword" class="form-label">Confirmar Nova Senha</label>
        <input 
          type="password" 
          id="confirmPassword" 
          class="form-control" 
          v-model="confirmPassword" 
          placeholder="Confirme sua nova senha"
          @input="validateConfirmPassword"
          :disabled="authStore.profileSaving"
          :required="isPasswordChangeActive"
        />
        <div v-if="errors.confirmPassword" class="text-danger mt-1">{{ errors.confirmPassword }}</div>
      </div>

      <div class="d-grid mt-4 profile-footer">
        <button type="submit" class="btn btn-color-site btn-lg" :disabled="isSaveButtonDisabled">
          <span v-if="authStore.profileSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ authStore.profileSaving ? 'Salvando...' : 'Salvar Alterações' }}
        </button>
      </div>
    </form>

    <BottomModal 
      :visible="showConfirmationModal" 
      @close="cancelSaveConfirmation"
      :fullHeight="false"
    >
      <template #default>
        <div class="confirmation-content p-3">
          <h5 class="text-center mb-3 text-primary-emphasis">Confirmar Alterações do Perfil</h5>
          <p class="text-muted text-center mb-4">Por favor, revise os dados antes de confirmar. Sua senha atual será verificada.</p>
          
          <div class="summary-item mb-2">
            <span class="fw-bold">Nome:</span> {{ name }} 
          </div>
          <div class="summary-item mb-2">
            <span class="fw-bold">E-mail:</span> {{ email }}
          </div>
          <div class="summary-item mb-3">
            <span class="fw-bold">Celular:</span> {{ phone }}
          </div>
          <div class="summary-item mb-3" v-if="isPasswordChangeActive">
            <span class="fw-bold">Nova Senha:</span> ********
          </div>

          <div class="d-flex gap-3 mt-4"> 
            <button class="btn btn-outline-secondary flex-fill" @click="cancelSaveConfirmation">
                <i class="fas fa-times me-2"></i> Voltar e Editar
            </button>
            <button class="btn btn-color-site flex-fill" @click="confirmSaveProfile" :disabled="authStore.profileSaving">
                <span v-if="authStore.profileSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ authStore.profileSaving ? 'Confirmando...' : 'Confirmar e Salvar' }}
            </button>
          </div>
        </div>
      </template>
    </BottomModal>

    <BottomModal
      :visible="showErrorModal"
      @close="showErrorModal = false"
      :fullHeight="false"
    >
      <template #default>
        <div class="error-modal-content p-3 text-center">
          <i class="fas fa-exclamation-circle text-danger mb-3" style="font-size: 3rem;"></i>
          <h5 class="mb-3 text-danger">Ocorreu um Erro!</h5>
          <p class="text-muted mb-4">{{ apiErrorMessage || 'Não foi possível carregar ou salvar seus dados de perfil no momento. Por favor, tente novamente mais tarde.' }}</p>
          <div class="d-grid">
            <button class="btn btn-outline-secondary" @click="showErrorModal = false">Entendido</button>
          </div>
        </div>
      </template>
    </BottomModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'; 
import BottomModal from '@/components/BottomModal.vue'; 
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'vue3-toastify'; 
import { isValidBRPhone } from '@/utils/phoneValidator';

const authStore = useAuthStore(); 

const name = ref(''); 
const email = ref(''); 
const phone = ref('');
const phoneMask = '(##) #####-####'; 

const currentPassword = ref('');
const password = ref(''); // Renomeado de newPassword para password
const confirmPassword = ref(''); // Renomeado de confirmNewPassword para confirmPassword

const showConfirmationModal = ref(false); 
const showErrorModal = ref(false);   
const apiErrorMessage = ref(''); 

const errors = ref({});

const isPasswordChangeActive = computed(() => {
  return password.value.trim() !== '' || confirmPassword.value.trim() !== ''; // Ajustado
});

const isSaveButtonDisabled = computed(() => {
  if (authStore.profileLoading || authStore.profileSaving) {
    return true;
  }
  if (authStore.profileError) {
    return true;
  }
  return !validateForm(false); 
});

const validateName = () => { 
  errors.value.name = ''; 
  if (!name.value.trim()) { 
    errors.value.name = 'O nome completo é obrigatório.';
    return false;
  }
  if (name.value.trim().length < 3) { 
    errors.value.name = 'O nome completo deve ter pelo menos 3 caracteres.';
    return false;
  }
  return true;
};

const validatePhone = () => {
  errors.value.phone = '';
  if (!phone.value.trim()) {
    errors.value.phone = 'O celular é obrigatório.';
    return false;
  }
  if (!isValidBRPhone(phone.value)) {
    errors.value.phone = 'Informe um celular válido com DDD (ex: (11) 98765-4321).';
    return false;
  }
  return true;
};

const validateCurrentPassword = () => {
  errors.value.currentPassword = '';
  if (isPasswordChangeActive.value && !currentPassword.value.trim()) {
    errors.value.currentPassword = 'A senha atual é obrigatória para alterar a senha.';
    return false;
  }
  return true;
};

const validatePassword = () => { // Renomeado de validateNewPassword para validatePassword
  errors.value.password = ''; // Ajustado
  if (isPasswordChangeActive.value && password.value.trim().length < 6) { // Ajustado
    errors.value.password = 'A nova senha deve ter no mínimo 6 caracteres.'; // Ajustado
    return false;
  }
  return true;
};

const validateConfirmPassword = () => { // Renomeado de validateConfirmNewPassword para validateConfirmPassword
  errors.value.confirmPassword = ''; // Ajustado
  if (isPasswordChangeActive.value && confirmPassword.value.trim() !== password.value.trim()) { // Ajustado
    errors.value.confirmPassword = 'As senhas não coincidem.'; // Ajustado
    return false;
  }
  return true;
};

const validateForm = (isSubmit = false) => {
  let isValid = true;
  if (isSubmit) errors.value = {}; 

  if (!validateName()) isValid = false; 
  if (!validatePhone()) isValid = false;

  if (isPasswordChangeActive.value) {
    if (!validateCurrentPassword()) isValid = false;
    if (!validatePassword()) isValid = false; // Ajustado
    if (!validateConfirmPassword()) isValid = false; // Ajustado
  } else if (isSubmit) {
    if (currentPassword.value.trim() !== '' || password.value.trim() !== '' || confirmPassword.value.trim() !== '') { // Ajustado
      errors.value.password = 'Para alterar a senha, preencha todos os campos de senha.'; // Ajustado
      isValid = false;
    }
  }

  return isValid;
};

const requestSaveConfirmation = () => {
  if (!validateForm(true)) {
    toast.error("Por favor, corrija os erros no formulário.");
    const firstErrorField = Object.keys(errors.value)[0];
    if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
    }
    return;
  }
  showConfirmationModal.value = true;
};

const cancelSaveConfirmation = () => {
  showConfirmationModal.value = false;
  if (isPasswordChangeActive.value) {
    currentPassword.value = '';
    password.value = ''; // Ajustado
    confirmPassword.value = ''; // Ajustado
    delete errors.value.currentPassword;
    delete errors.value.password; // Ajustado
    delete errors.value.confirmPassword; // Ajustado
  }
};

const confirmSaveProfile = async () => {
  apiErrorMessage.value = ''; 

  // --- Função auxiliar para limpar e formatar o telefone ---
  const formatPhoneForApi = (phoneNumber) => {
    // Remove todos os caracteres não numéricos
    const cleaned = phoneNumber.replace(/\D/g, '');
    // Adiciona o prefixo internacional se ainda não existir
    if (!cleaned.startsWith('55')) {
      return '+55' + cleaned;
    }
    return '+' + cleaned;
  };

  const payload = {
    name: name.value,
    phone: formatPhoneForApi(phone.value),
  };

  if (isPasswordChangeActive.value) {
    payload.currentPassword = currentPassword.value;
    payload.password = password.value; // Renomeado de newPassword para password
  }

  try {
    await authStore.updateProfile(payload);
    
    toast.success('Perfil atualizado com sucesso!');
    showConfirmationModal.value = false; 

    currentPassword.value = '';
    password.value = ''; // Ajustado
    confirmPassword.value = ''; // Ajustado

  } catch (err) {
    console.error('Erro ao salvar dados do perfil:', err);
    showConfirmationModal.value = false;
    let errorMessage = 'Erro ao atualizar perfil. Por favor, tente novamente.';

    if (err.response) {
      if (err.response.status === 401 || err.response.status === 403) { 
        errorMessage = err.response.data.message || 'Senha atual incorreta. Por favor, verifique sua senha.';
        errors.value.currentPassword = errorMessage;
        currentPassword.value = ''; 
      } else if (err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
        showErrorModal.value = true; 
      } else {
        showErrorModal = true; 
      }
    } else {
      showErrorModal.value = true;
    }
    apiErrorMessage.value = errorMessage;
    toast.error(errorMessage);

    if (errors.value.currentPassword) {
        nextTick(() => {
            document.getElementById('currentPassword')?.focus();
        });
    }

  } finally {
    //
  }
};

onMounted(async () => {
  await authStore.fetchUserProfile(); 
});

watch(() => authStore.user, (newUser) => {
  console.log('Watcher acionado. Novo usuário na store:', newUser);
  if (newUser) {
    name.value = newUser.name || newUser.fullName || ''; 
    email.value = newUser.email || '';

    if (newUser.phone) {
        let cleanPhone = newUser.phone.replace(/\D/g, '');
        console.log('Telefone da store (limpo inicialmente):', cleanPhone);
        if (cleanPhone.startsWith('55') && cleanPhone.length > 2) {
            cleanPhone = cleanPhone.substring(2); 
            console.log('Telefone sem 55 (para máscara):', cleanPhone);
        }
        phone.value = cleanPhone; 
        console.log('phone.value definido para:', phone.value);
    } else {
        phone.value = '';
        console.log('phone.value limpo (sem telefone na store)');
    }
  } else {
    name.value = '';
    email.value = '';
    phone.value = '';
    console.log('Usuário da store é nulo, campos limpos.');
  }
}, { immediate: true });
</script>

<style scoped>
/* Seu CSS permanece inalterado */
.profile-edit-container {
 max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

@media (min-width: 768px) {
  .profile-edit-container {
    padding: 3rem;
  }
}

.text-primary-emphasis {
  color: #32355d !important;
}

.description-text {
  font-size: 0.95rem;
  line-height: 1.4;
  color: #6c757d; 
  margin-bottom: 1.5rem; 
}

.form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control, .form-select {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  font-size: 1rem;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-control:focus, .form-select:focus {
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25);
  outline: none;
}

.form-control[readonly], .form-control:disabled {
    background-color: #e9ecef; /* Um cinza mais claro */
    opacity: 0.8;
    cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text {
    font-size: 0.875em;
    color: #6c757d;
}

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.btn-color-site:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border {
  vertical-align: -0.125em;
}

.confirmation-content h5 {
  font-size: 1.25rem;
  font-weight: 600;
}

.confirmation-content p {
  font-size: 0.95rem;
}

.summary-item {
  font-size: 1rem;
  color: #32355d;
}

.summary-item .fw-bold {
  color: #32355d;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
}

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
}
.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
}
.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #495057;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.btn i.me-2 {
  margin-right: 0.5rem;
}

@media (max-width: 767.98px) {
  .d-flex.flex-column.flex-md-row {
    flex-direction: column !important;
  }
  .w-100 {
    width: 100% !important;
  }
  .w-md-auto {
    width: auto !important; 
  }
  .profile-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.75rem 1rem;
    background-color: #fff;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.08);
    z-index: 1031;
    margin-top: 0;
  }
  .profile-edit-form {
    padding-bottom: 80px;
  }
}

.error-modal-content i {
  color: #dc3545;
}
.error-modal-content h5 {
  color: #dc3545;
}
</style>